import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, Eye } from 'lucide-react';
import apiClient from '../services/apiClient';
import { toast } from 'sonner';
/**
 * <PreviewAsStaffModal /> — pick WHICH crew member to preview.
 *
 * ROOT CAUSE this exists to fix. "Preview as Staff" used to flip the chrome
 * to the staff role while still being the ADMIN's own user. An admin has no
 * `db.staff` record, so the staff app answered honestly for that user:
 * "No assignments yet", "$0.00 Paid to You". The menu item promised "See
 * what your team sees" and showed a view that belongs to nobody — which
 * reads as "the staff app is broken" when it is fine.
 *
 * A role is not a person. Previewing a role can only ever show empty chrome;
 * previewing a PERSON shows their real rows. The picker below is the
 * difference.
 *
 * Read-only throughout. The payload comes from
 * `GET /staff/admin/assignments-preview/{staffId}`, which runs the SAME
 * function `/staff/my-assignments` runs and is scoped to the caller's own
 * staff — someone else's crew 404s.
 */
export default function PreviewAsStaffModal({ open, onClose, onPick }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/staff');
        const rows = Array.isArray(res.data) ? res.data : (res.data?.staff || []);
        if (!cancelled) setStaff(rows);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load crew');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);
  const nameOf = (s) => (
    s?.name || `${s?.firstName || ''} ${s?.lastName || ''}`.replace(/\s+/g, ' ').trim() || 'Unnamed'
  );
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (staff || []).filter((s) => s && (s.id || s._id));
    const matched = q
      ? list.filter((s) => nameOf(s).toLowerCase().includes(q) || String(s.email || '').toLowerCase().includes(q))
      : list;
    return matched.slice().sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [staff, query]);
  if (!open) return null;
  const pick = (s) => {
    const id = String(s.id || s._id || '');
    if (!id) return;
    onPick(id, nameOf(s));
  };
  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 py-16"
      style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(3px)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="preview-as-staff-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: '#0E1220', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Eye className="w-3.5 h-3.5" style={{ color: '#34D399' }} />
          <p className="text-[12.5px] font-semibold text-white/85">Preview as which crew member?</p>
          <button
            onClick={onClose}
            className="ml-auto p-1 rounded-full hover:bg-white/[0.06] transition"
            data-testid="preview-as-staff-close"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5 text-white/45" />
          </button>
        </div>
        <div className="px-4 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-white/30 shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search crew..."
              className="w-full bg-transparent outline-none text-[12.5px] text-white/85 placeholder:text-white/25"
              data-testid="preview-as-staff-search"
            />
          </div>
        </div>
        <div className="max-h-[52vh] overflow-y-auto py-1.5">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-white/35">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-[12px]">Loading crew...</span>
            </div>
          )}
          {!loading && rows.length === 0 && (
            <p className="py-10 text-center text-[12px] text-white/35" data-testid="preview-as-staff-empty">
              {query ? `No crew matching "${query}"` : 'No crew found'}
            </p>
          )}
          {!loading && rows.map((s) => {
            const id = String(s.id || s._id);
            const label = nameOf(s);
            const initials = label.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
            return (
              <button
                key={id}
                onClick={() => pick(s)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-white/[0.04] transition text-left"
                data-testid="preview-as-staff-row"
              >
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[9.5px] font-bold shrink-0"
                  style={{ background: 'linear-gradient(135deg,#E7C75E,#D4AF37)', color: '#1a1a1a' }}
                >
                  {initials || '?'}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[12.5px] text-white/85 truncate">{label}</span>
                  {s.email ? <span className="block text-[10.5px] text-white/35 truncate">{s.email}</span> : null}
                </span>
                {s.status ? (
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/30 shrink-0">{s.status}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <p className="px-4 py-2.5 text-[10.5px] text-white/35" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          Read-only. Opens their My Assignments exactly as they see it.
        </p>
      </div>
    </div>,
    document.body
  );
}
